import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { HelpCircle, ExternalLink, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLocation } from "wouter";
import ReactMarkdown from 'react-markdown';

interface HelpContent {
  id: string;
  key: string;
  content: string;
  category: string;
  updated_at: string;
}

export function FloatingHelpButton() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [location] = useLocation();

  // Get help content from API with aggressive caching
  const { data: helpContent, isLoading } = useQuery<HelpContent[]>({
    queryKey: ['/api/help'],
    queryFn: async () => {
      const response = await fetch('/api/help');
      if (!response.ok) {
        throw new Error('Failed to fetch help content');
      }
      return response.json();
    },
    enabled: isModalOpen,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (react-query v5)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Determine current module for contextual help
  const getCurrentModule = () => {
    const path = location;
    if (path.includes('/maturity')) return 'maturity';
    if (path.includes('/value')) return 'value-engineering';
    if (path.includes('/benchmarking')) return 'benchmarking';
    if (path.includes('/roi')) return 'roi';
    if (path.includes('/admin')) return 'admin';
    if (path.includes('/project')) return 'project-management';
    return 'general';
  };

  // Fast contextual help filtering with memoization
  const contextualHelp = useMemo<HelpContent[]>(() => {
    if (!helpContent) return [];
    
    const currentModule = getCurrentModule();
    
    // Map module names to category names
    const categoryMap: Record<string, string[]> = {
      'maturity': ['Maturity Assessment', 'General'],
      'value-engineering': ['Value Engineering', 'General'],
      'benchmarking': ['Benchmarking', 'General'],
      'roi': ['ROI Analysis', 'General'],
      'admin': ['Admin Functions', 'General'],
      'project-management': ['User Management', 'Dashboard', 'General'],
      'general': ['General']
    };
    
    const relevantCategories = categoryMap[currentModule] || ['General'];
    
    const moduleHelp = helpContent.filter((item: HelpContent) => 
      relevantCategories.includes(item.category) ||
      item.key.toLowerCase().includes(currentModule.replace('-', '_'))
    );
    
    // Return up to 3 most relevant items
    return moduleHelp.slice(0, 3);
  }, [helpContent, location]);

  const [, setLocation] = useLocation();
  
  const openHelpCenter = () => {
    setLocation('/help-center');
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Floating Help Button */}
      <div className="fixed bottom-20 right-6 z-50">
        <Button
          onClick={() => setIsModalOpen(true)}
          className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 bg-primary hover:bg-[#27C4C5] text-forestBlack"
          size="icon"
          title="Get Help"
        >
          <HelpCircle className="h-5 w-5" />
        </Button>
      </div>

      {/* Help Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]" aria-describedby="help-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-white" />
              Help & Support
            </DialogTitle>
          </DialogHeader>
          
          <div id="help-description" className="sr-only">
            Access help documentation and support resources for the current module.
          </div>

          <div className="space-y-4">
            {/* Quick Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                onClick={openHelpCenter}
                className="flex items-center gap-2 bg-[#4AE2C9] hover:bg-[#3BC4A8] text-[#0D1224]"
              >
                <BookOpen className="h-4 w-4" />
                Browse Help Center
              </Button>
              <Badge variant="secondary" className="px-3 py-1">
                {getCurrentModule().replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} Module
              </Badge>
            </div>

            {/* Contextual Help Content */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground">
                Help for This Section
              </h3>
              
              <ScrollArea className="h-[400px] pr-4">
                {isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    ))}
                  </div>
                ) : contextualHelp.length > 0 ? (
                  <div className="space-y-4">
                    {contextualHelp.map((item) => (
                      <div key={item.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm">
                            {item.key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {item.category}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground prose prose-sm max-w-none prose-headings:text-foreground prose-strong:text-foreground">
                          <ReactMarkdown>{item.content}</ReactMarkdown>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <HelpCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No help content available for this section.</p>
                    <p className="text-xs mt-1">
                      Try the full Help Center for comprehensive documentation.
                    </p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Footer Actions */}
            <div className="border-t pt-4 flex justify-between items-center text-xs text-muted-foreground">
              <span>Need more help? Contact support</span>
              <Button
                variant="link"
                size="sm"
                onClick={openHelpCenter}
                className="h-auto p-0 text-xs"
              >
                Browse All Topics →
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
